import { IconInbox } from "@tabler/icons-react";

import { useActionCenter } from "~/contexts/actionCenterProvider";
import { useInboxEvents } from "~/hooks/useInboxEvents";
import { ActionIcon, Indicator, Tooltip } from "~/ui";
import { api } from "~/utils/api";

import styles from "./ActionCenterButton.module.css";

const badgeLabelFor = (count: number): number | string => (count > 99 ? "99+" : count);

export const ActionCenterButton = (): JSX.Element => {
  const actionCenterContext = useActionCenter();
  const countsQuery = api.inbox.counts.useQuery();

  // Always mounted (`Header.tsx`) regardless of whether the Inbox panel is open — the live badge
  // updates even while the user is elsewhere in the app (R6).
  useInboxEvents();

  const toggleOpened = () => {
    actionCenterContext.setIsOpen(!actionCenterContext.state.isOpen);
  };

  const unreadNotifications = countsQuery.data?.unreadNotifications ?? 0;
  const openTasks = countsQuery.data?.openTasks ?? 0;
  // Round 24, per user: ONE number, not a green notification badge stacked on a blue task one. Two
  // badges on a 23px icon forced both to 16px and still collided, and the split asked the reader to
  // add them up before knowing whether the Inbox wanted attention. The panel itself still breaks the
  // total down by kind in its header (`inbox-header-counts.ts`), which is where that detail belongs.
  const waitingForYou = unreadNotifications + openTasks;

  return (
    <Tooltip label="Click here to open your Inbox" position="left" withArrow>
      <ActionIcon aria-label="Inbox" aria-expanded={actionCenterContext.state.isOpen} size={44} radius="sm" onClick={toggleOpened}>
        {/* Keep the badge and its pulse inside the button so every visible part opens the Inbox. */}
        <Indicator
          color="red"
          disabled={waitingForYou === 0}
          label={badgeLabelFor(waitingForYou)}
          offset={4}
          processing={waitingForYou > 0}
          size={16}
          zIndex={1}
        >
          <span className={styles.inboxIcon}>
            <IconInbox size={15} />
          </span>
        </Indicator>
      </ActionIcon>
    </Tooltip>
  );
};
